import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatCoding, formatQuantity, formatReferenceString } from '@medplum/core';
import {
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Task,
} from '@medplum/fhirtypes';
import { CHARTING_EXPORTED_PDF_TIMEZONE } from '@bariendo/lib/constants';
import { formatToAdobeSpectrumFormat } from '@/services/date';

dayjs.extend(utc);
dayjs.extend(timezone);

export type QuestionnaireOutput = {
  key: string;
  display?: string | null;
  response: QuestionnaireResponse;
};

function sanitizeAnswerString(value?: string): string {
  if (!value) {
    return '';
  }

  let result = value.trim();

  // Attempt to decode accidentally double-encoded JSON strings (e.g. "\"some text\"")
  if (result.startsWith('"') && result.endsWith('"')) {
    try {
      const decoded = JSON.parse(result);
      if (typeof decoded === 'string') {
        result = decoded.trim();
      }
    } catch {
      // Ignore JSON parse errors and fall back to best-effort cleanup
    }
  }

  // Unescape common escaped characters that sometimes leak from encoded content
  result = result.replace(/\\n/g, '\n').replace(/\\r/g, '').replace(/\\"/g, '"');

  // Strip obvious HTML tags while preserving the inner text
  if (/<\/?[a-z][^>]*>/i.test(result)) {
    result = result.replace(/<\/?[a-z][^>]*>/gi, '');
  }

  // If the string contains repeated `"value":"..."` patterns (even if not valid JSON),
  // extract and join them into a readable list.
  const valueMatches = Array.from(result.matchAll(/"value"\s*:\s*"([^"]*)"/g)).map((match) => match[1].trim());
  const nonEmptyValues = valueMatches.filter(Boolean);
  if (nonEmptyValues.length > 0) {
    return nonEmptyValues.join(', ');
  }

  // If the remaining string looks like JSON, try to extract human-readable content
  if ((result.startsWith('{') || result.startsWith('[')) && (result.endsWith('}') || result.endsWith(']'))) {
    try {
      const parsed: any = JSON.parse(result);

      // Handle body diagram-style data (e.g. Healthie annotations)
      if (parsed && typeof parsed === 'object' && parsed.view && parsed.notes) {
        const label = typeof parsed.notes?.label === 'string' ? parsed.notes.label.trim() : '';
        return label || '';
      }

      // Generic handler for arrays/objects of { value: string }
      const values: string[] = [];

      const collectValues = (node: any): void => {
        if (!node) return;

        if (Array.isArray(node)) {
          node.forEach(collectValues);
          return;
        }

        if (typeof node === 'object') {
          if (typeof node.value === 'string' && node.value.trim()) {
            values.push(node.value.trim());
          }

          Object.values(node).forEach(collectValues);
        }
      };

      collectValues(parsed);

      if (values.length > 0) {
        return values.join(', ');
      }

      // For other complex JSON structures, avoid showing raw blobs
      return '';
    } catch {
      // If JSON parsing fails, continue with best-effort cleanup
    }
  }

  // Collapse excessive whitespace
  return result.replace(/\s+/g, ' ').trim();
}

export function getQuestionnaireOutputs(tasks: Task[]): QuestionnaireOutput[] {
  return tasks.flatMap((task, taskIndex) => {
    if (!task.output) return [];

    return task.output.flatMap((output, outputIndex) => {
      const valueReference = output.valueReference as typeof output.valueReference & {
        resource?: QuestionnaireResponse;
      };
      const resource = valueReference?.resource;

      if (!resource || resource.resourceType !== 'QuestionnaireResponse') {
        return [];
      }

      return [
        {
          key: resource.id ?? `${task.id ?? taskIndex}-${outputIndex}`,
          display: task.focus?.display || resource.item?.[0]?.text,
          response: resource,
        },
      ];
    });
  });
}

export function formatQuestionnaireAnswer(answer: QuestionnaireResponseItemAnswer): string | undefined {
  if (isAnswerOfType(answer, 'valueString')) return sanitizeAnswerString(answer.valueString);
  if (isAnswerOfType(answer, 'valueBoolean')) return answer.valueBoolean ? 'Yes' : 'No';
  if (isAnswerOfType(answer, 'valueDecimal')) return String(answer.valueDecimal);
  if (isAnswerOfType(answer, 'valueInteger')) return String(answer.valueInteger);
  if (isAnswerOfType(answer, 'valueDate')) return answer.valueDate;
  if (isAnswerOfType(answer, 'valueDateTime')) {
    if (!answer.valueDateTime) return '';
    const date = dayjs(answer.valueDateTime).tz(CHARTING_EXPORTED_PDF_TIMEZONE);
    return `${formatToAdobeSpectrumFormat(date)} EDT`;
  }
  if (isAnswerOfType(answer, 'valueTime')) return answer.valueTime;
  if (isAnswerOfType(answer, 'valueUri')) return answer.valueUri;
  if (isAnswerOfType(answer, 'valueAttachment')) {
    // For attachments, return the title or a generic label
    return answer.valueAttachment?.title || 'Attachment';
  }
  if (isAnswerOfType(answer, 'valueCoding')) return formatCoding(answer.valueCoding);
  if (isAnswerOfType(answer, 'valueQuantity')) return formatQuantity(answer.valueQuantity);
  if (isAnswerOfType(answer, 'valueReference')) return formatReferenceString(answer.valueReference);
  return undefined;
}

function isAnswerOfType(answer: QuestionnaireResponseItemAnswer, type: keyof QuestionnaireResponseItemAnswer): boolean {
  return answer[type] !== undefined && answer[type] !== null;
}

export function getQuestionnaireSearchableText(tasks: Task[] = []): string {
  const outputs = getQuestionnaireOutputs(tasks);
  const parts: string[] = [];

  outputs.forEach(({ display, response }) => {
    if (display) {
      parts.push(display);
    }

    response.item?.forEach((item) => {
      collectQuestionnaireItemText(item, parts);
    });
  });

  return parts.join(', ');
}

function collectQuestionnaireItemText(item: QuestionnaireResponseItem, parts: string[]): void {
  if (item.text) {
    parts.push(item.text);
  }

  item.answer?.forEach((answer) => {
    const formatted = formatQuestionnaireAnswer(answer);
    if (formatted) {
      parts.push(formatted);
    }

    answer.item?.forEach((nestedItem) => collectQuestionnaireItemText(nestedItem, parts));
  });

  item.item?.forEach((nestedItem) => collectQuestionnaireItemText(nestedItem, parts));
}
